// src/services/extDocumentService.ts
import { hmacMultipart } from './hmacAxios.ts';

// MIME types permitidos por negocio (mismo set del servicio interno)
export const ALLOWED_MIME_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/svg+xml',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/csv',
];

export const DEFAULT_MAX_FILE_SIZE_MB = 25;

const sleep = (ms: number) => new Promise(r => setTimeout(r, ms));

const isTransientError = (status?: number) =>
  status === 408 || status === 425 || status === 429 || (status !== undefined && status >= 500 && status <= 599);

type UploadParams = {
  file: File | Blob;
  category: string;
  originType: string;
  originId: string | number;
  description?: string;
  tags?: string | string[] | { label?: string; value?: string }[];
  // reintentos / callbacks
  retryAttempts?: number; // default 2
  onUploadProgress?: (evt: { loaded: number; total?: number; percent?: number }) => void;
  signal?: AbortSignal;
};

/**
 * Normaliza tags a un array de strings para enviar como campos múltiples.
 * Spring Boot con @ModelAttribute y List<String> espera múltiples campos con el mismo nombre.
 */
function normalizeTagsToArray(tags: UploadParams['tags']): string[] {
  if (!tags) return [];
  if (Array.isArray(tags)) {
    return tags
      .map((t: any) => (typeof t === 'string' ? t : (t?.label || t?.value || '')))
      .filter(Boolean);
  }
  if (typeof tags === 'string') {
    // Si viene como CSV, convertir a array
    return tags.split(',').map(t => t.trim()).filter(Boolean);
  }
  return [];
}

function validateFile(
  file: File | Blob,
  { maxSizeMB = DEFAULT_MAX_FILE_SIZE_MB, allowedTypes = ALLOWED_MIME_TYPES } = {}
) {
  if (!file) throw new Error('No se seleccionó ningún archivo.');
  const type = (file as any).type as string | undefined;
  if (type && !allowedTypes.includes(type)) {
    throw new Error(`Tipo de archivo no permitido: ${type || 'desconocido'}`);
  }
  const maxBytes = maxSizeMB * 1024 * 1024;
  const size = (file as any).size ?? 0;
  if (size > maxBytes) {
    throw new Error(`El archivo supera el tamaño máximo (${maxSizeMB} MB).`);
  }
}

/**
 * Sube un documento al endpoint externo HMAC:
 *   POST /document-service/ext/documents/upload  (multipart/form-data)
 * Devuelve { documentId, details } con el cuerpo del backend.
 */
export async function uploadDocumentExt({
  file,
  category,
  originType,
  originId,
  description,
  tags,
  retryAttempts = 2,
  onUploadProgress,
  signal,
}: UploadParams): Promise<{ documentId: number | string | undefined; details: any }> {
  validateFile(file);

  // Preparar campos - los enviaremos en el multipart body
  // hmacMultipart los incluirá como "query params virtuales" para la firma HMAC
  const fieldValues: Record<string, string | string[]> = {};
  fieldValues['category']   = String(category ?? '');
  fieldValues['originType'] = String(originType ?? '').trim();
  fieldValues['originId']   = String(originId ?? '');
  if (description != null && String(description).length > 0) {
    fieldValues['description'] = String(description);
  }
  // Tags como array para que Spring lo parsee como List<String>
  const tagsArray = normalizeTagsToArray(tags);
  if (tagsArray.length > 0) {
    fieldValues['tags'] = tagsArray;
  }

  // Archivo con filename y contentType explícitos
  const guessedName = (file as any).name || `file-${Date.now()}`;
  const guessedType = (file as any).type || 'application/octet-stream';

  const files = [
    {
      field: 'file',
      file,
      filename: guessedName,
      contentType: guessedType,
    },
  ];

  const urlPath = '/document-service/ext/documents/upload';

  // shim de progreso: 0% al inicio
  if (typeof onUploadProgress === 'function') {
    try {
      const total = (file as any).size ?? undefined;
      onUploadProgress({ loaded: 0, total, percent: total ? 0 : undefined });
    } catch {}
  }

  const attemptOnce = async () => {
    console.log('📤 [extDocumentService] Uploading with fields:', fieldValues);
    console.log('📤 [extDocumentService] Uploading file:', {
      name: guessedName,
      type: guessedType,
      size: (file as any).size
    });
    
    // Enviar campos SOLO en multipart (no en query params)
    // hmacMultipart los incluirá como "query params virtuales" en la firma HMAC
    const resp = await hmacMultipart<any>(urlPath, fieldValues, files, undefined, {
      // Enviar el payload firmado sin transformación adicional
      onUploadProgress: (evt) => {
        if (typeof onUploadProgress === 'function' && evt && evt.total) {
          const percent = Math.round((evt.loaded / (evt.total || 1)) * 100);
          try { onUploadProgress({ loaded: evt.loaded, total: evt.total, percent }); } catch {}
        }
      },
      signal,
    });
    
    console.log('✅ [extDocumentService] Upload response:', resp);

    const data = resp?.data ?? resp;

    // Interpretaciones comunes del backend
    let documentId: number | string | undefined;
    if (typeof data === 'number' || typeof data === 'string') {
      documentId = data as any;
    } else if (data && typeof data === 'object') {
      documentId = data.documentId ?? data.id ?? data.documentID;
    }

    // shim de progreso: 100% al final
    if (typeof onUploadProgress === 'function') {
      try {
        const total = (file as any).size ?? undefined;
        onUploadProgress({ loaded: total ?? 0, total, percent: total ? 100 : undefined });
      } catch {}
    }

    return { documentId, details: data };
  };

  let lastErr: any = null;
  for (let attempt = 0; attempt <= retryAttempts; attempt++) {
    try {
      return await attemptOnce();
    } catch (err: any) {
      lastErr = err;
      const status = err?.response?.status ?? err?.status;
      if (err?.__aborted || !isTransientError(status) || attempt === retryAttempts) {
        throw err;
      }
      const backoff = 500 * Math.pow(2, attempt); // 500, 1000, 2000…
      await sleep(backoff);
    }
  }
  throw lastErr || new Error('Fallo de subida (ext)');
}

const extDocumentService = {
  uploadDocumentExt,
  ALLOWED_MIME_TYPES,
  DEFAULT_MAX_FILE_SIZE_MB,
};

export default extDocumentService;
